import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {of, switchMap, Observable, Subject, EMPTY} from "rxjs";
import {retry, catchError, takeUntil} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {FintechService} from "../../../services/fintech.service";
import {ToastrService} from "ngx-toastr";

@Component({
    selector: 'app-fintech-contact',
    templateUrl: './fintech-contact.component.html',
    styleUrls: ['./fintech-contact.component.scss']
})
export class FintechContactComponent implements  OnInit, OnDestroy {
    contactInfo;
    contactForm: FormGroup;
    private destroy$ = new Subject<void>();

    constructor(public fintechService: FintechService,
                private formBuilder: FormBuilder,
                private http: HttpClient,
                private toastr: ToastrService) {
        this.contactForm = this.formBuilder.group({
            company: ['', [Validators.required, Validators.minLength(2)]],
            vat: ['', [Validators.required, Validators.minLength(2)]],
            firstName: ['', [Validators.required, Validators.minLength(2)]],
            lastName: ['', [Validators.required, Validators.minLength(2)]],
            phone1: ['', [Validators.required, Validators.minLength(2)]],
            phone2: ['', [Validators.required, Validators.minLength(2)]],
            email: ['', [Validators.required, Validators.minLength(2)]],
        });

        this.fintechService.notify$.pipe(takeUntil(this.destroy$)).subscribe( msg => {
            if (this.fintechService.data.contactInfo != null) {
                this.contactInfo = this.fintechService.data.contactInfo;
            }
        });

        this.fintechService.busy$.pipe(takeUntil(this.destroy$)).subscribe( busy => {
            if (busy) {
                this.contactForm.disable();
            } else {
                this.contactForm.enable();
            }
        });

    }

    ngOnInit(): void {
    }

    onEditContact() {
        this.fintechService.editMode = "contact";
        this.editContact();
    }

    editContact() {
        this.contactForm.get('company').patchValue(this.contactInfo.company, {emitEvent: false});
        this.contactForm.get('vat').patchValue(this.contactInfo.vat, {emitEvent: false});
        this.contactForm.get('firstName').patchValue(this.contactInfo.firstName, {emitEvent: false});
        this.contactForm.get('lastName').patchValue(this.contactInfo.lastName, {emitEvent: false});
        this.contactForm.get('phone1').patchValue(this.contactInfo.phone1, {emitEvent: false});
        this.contactForm.get('phone2').patchValue(this.contactInfo.phone2, {emitEvent: false});
        this.contactForm.get('email').patchValue(this.contactInfo.email, {emitEvent: false});
    }

    onContactCancel() {
        this.fintechService.editMode = "info";
    }

    onContactSubmit() {
        if (!this.contactForm.valid) {
            this.toastr.error('Please fill out all required fields correctly.', 'Validation Error');
            return;
        }

        this.fintechService.setBusy(true);

        this.contactInfo.company =  this.contactForm.value.company;
        this.contactInfo.vat =  this.contactForm.value.vat;
        this.contactInfo.firstName =  this.contactForm.value.firstName;
        this.contactInfo.lastName =  this.contactForm.value.lastName;
        this.contactInfo.phone1 =  this.contactForm.value.phone1;
        this.contactInfo.phone2 =  this.contactForm.value.phone2;
        this.contactInfo.email =  this.contactForm.value.email;

        const obs$ = this.fintechService.getPayerToken().pipe(
            switchMap( payerToken => {
                const options: any = {
                    token: payerToken,
                    company: this.contactInfo.company,
                    vat: this.contactInfo.vat,
                    firstName: this.contactInfo.firstName,
                    lastName: this.contactInfo.lastName,
                    phone1: this.contactInfo.phone1,
                    phone2: this.contactInfo.phone2,
                    email: this.contactInfo.email
                };
                const url = "https://pay.signage.me:8443/fintech/updateContactInfo";
                return this.http.post(url, options).pipe(
                    retry({
                        count: 5,
                        delay: (error, retryCount) => {
                            this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, { timeOut: 3000 });
                            return of(error);
                        },
                    }),
                    catchError(error => {
                        console.error('Error updating contact info after retries:', error);
                        this.toastr.error('Failed to update contact information. Please try again.', 'FinTech', { timeOut: 5000 });
                        this.fintechService.setBusy(false);
                        return EMPTY;
                    }),
                );
            }),
            switchMap(ret => {
                this.fintechService.editMode = "info";
                this.fintechService.setBusy(false);
                this.toastr.success('Contact information updated successfully.', 'FinTech');
                return of('a');
            }),
            takeUntil(this.destroy$)
        );
        obs$.subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
